import {
  Component,
  DoCheck,
  OnChanges,
  Input,
  SimpleChanges,
  Optional,
  EventEmitter,
  Output,
  OnDestroy,
  Attribute,
  ViewChild,
  ElementRef,
  NgZone,
} from "@angular/core";
import {
  FormGroup,
  FormArray,
  FormGroupDirective,
  FormControl,
} from "@angular/forms";
import {
  KreFormFieldConfig,
  KreFormOptions,
  KreFormOptionsCache,
} from "./kre-form.field.config";
import { KreFormOther } from "../services/kre-form.other";
import { KreFormBuilder } from "../services/kre-form.builder";
import { KreFormConfig } from "../services/kre-form.config";
import {
  assignFieldValue,
  isNullOrUndefined,
  wrapProperty,
  clone,
  defineHiddenProp,
  getKeyPath,
  isObject,
} from "../utils";
import { Subscription, Subject } from "rxjs";
import {
  debounceTime,
  switchMap,
  distinctUntilChanged,
  take,
} from "rxjs/operators";

import { clearControl } from "../extensions/field-form/utils";

@Component({
  selector: "kreform",
  template: `
    <ng-container *ngFor="let field of fields">
      <kreform-field
        *ngIf="checkFieldShow(field)"
        hide-deprecation
        [form]="field.form"
        [options]="field.options"
        [model]="field.model"
        [lang]="lang"
        [field]="field"
        [status]="status"
      >
      </kreform-field>
    </ng-container>
    <ng-container #content>
      <ng-content></ng-content>
    </ng-container>
  `,
  host: {
    "[class]": "class",
  },
  providers: [KreFormBuilder],
})
export class KreForm implements DoCheck, OnChanges, OnDestroy {
  @Input() form: FormGroup | FormArray;

  @Input()
  set model(model: any) {
    this._model = this.immutable ? clone(model) : model;
  }
  get model() {
    if (!this._model) {
      this._model = {};
    }

    return this._model;
  }

  @Input()
  set fields(fields: KreFormFieldConfig[]) {
    this._fields = this.immutable ? clone(fields) : fields;
  }
  get fields() {
    return this._fields || [];
  }

  @Input()
  set options(options: KreFormOptions) {
    this._options = this.immutable ? clone(options) : options;
  }
  get options() {
    return this._options;
  }

  @Input() class: string;

  @Input() status: number = 0;

  @Input() lang: string = "";

  @Output() modelChange = new EventEmitter<any>();
  @ViewChild("content", { static: true }) setcontent(
    content: ElementRef<HTMLElement>
  ) {
    if (content) {
      let hasContent = false;
      let node = content.nativeElement.nextSibling;
      while (node && !hasContent) {
        if (
          node.nodeType === Node.ELEMENT_NODE ||
          (node.nodeType === Node.TEXT_NODE &&
            node.textContent &&
            node.textContent.trim() !== "")
        ) {
          hasContent = true;
        }
        node = node.nextSibling;
      }
      if (hasContent) {
      }
    }
  }

  private immutable = false;
  private _model: any;
  private _modelChangeValue: any = {};
  private _fields: KreFormFieldConfig[];
  private _options: KreFormOptions;
  private modelChangeSubs: Subscription[] = [];
  private modelChange$ = new Subject<void>();
  private modelChangeSub = this.modelChange$
    .pipe(switchMap(() => this.ngZone.onStable.asObservable().pipe(take(1))))
    .subscribe(() =>
      this.ngZone.runGuarded(() => {
        this.checkExpressionChange();
        this.modelChange.emit((this._modelChangeValue = clone(this.model)));
      })
    );

  constructor(
    private kreFormOther: KreFormOther,
    private formlyBuilder: KreFormBuilder,
    private formlyConfig: KreFormConfig,
    private ngZone: NgZone,
    @Attribute("immutable") immutable,
    @Optional() private parentFormGroup: FormGroupDirective
  ) {
    if (immutable !== null) {
    }
    this.immutable = immutable !== null || !!formlyConfig.extras.immutable;
  }

  ngDoCheck() {
    if (this.formlyConfig.extras.checkExpressionOn === "changeDetectionCheck") {
      this.checkExpressionChange();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.model && this.field) {
      this.field.model = this.model;
    }

    if (changes.fields && this.form) {
      clearControl(this.form);
    }

    if (
      changes.fields ||
      changes.form ||
      (changes.model && this._modelChangeValue !== changes.model.currentValue)
    ) {
      this.form = this.form || new FormGroup({});
      this.setOptions();
      this.options.updateInitialValue();
      this.clearModelSubscriptions();

      this.formlyBuilder.buildForm(
        this.form,
        this.fields,
        this.model,
        this.options
      );
      this.trackModelChanges(this.fields);
      this.options.updateInitialValue();

      this.trackModelChanges(this.fields);
    }
  }

  ngOnDestroy() {
    this.modelChangeSub.unsubscribe();
    this.clearModelSubscriptions();
  }

  changeModel({
    key,
    value,
    field,
  }: {
    key: string;
    value: any;
    field: KreFormFieldConfig;
  }) {
    assignFieldValue(field, value);
    this.modelChange$.next();
  }

  setOptions() {
    if (!this.options) {
      this.options = {};
    }

    if (!this.options.resetModel) {
      this.options.resetModel = (model?: any) => {
        model = clone(
          isNullOrUndefined(model)
            ? (<KreFormOptionsCache>this.options)._initialModel
            : model
        );
        if (this.model) {
          Object.keys(this.model).forEach((k) => delete this.model[k]);
          Object.assign(this.model, model || {});
        }

        (<KreFormOptionsCache>this.options)._buildForm();

        if (
          this.options.parentForm &&
          this.options.parentForm.control === this.form
        ) {
          this.options.parentForm.resetForm(this.model);
        } else {
          this.form.reset(this.model);
        }
      };
    }

    if (!this.options.parentForm && this.parentFormGroup) {
      defineHiddenProp(this.options, "parentForm", this.parentFormGroup);
      wrapProperty(this.options.parentForm, "submitted", ({ firstChange }) => {
        if (!firstChange) {
          this.checkExpressionChange();
          (<KreFormOptionsCache>this.options)._markForCheck({
            fieldGroup: this.fields,
            model: this.model,
            formControl: this.form,
            options: this.options,
          });
        }
      });
    }

    if (!this.options.updateInitialValue) {
      this.options.updateInitialValue = () =>
        ((<KreFormOptionsCache>this.options)._initialModel = clone(this.model));
    }

    if (!(<KreFormOptionsCache>this.options)._buildForm) {
      (<KreFormOptionsCache>this.options)._buildForm = (
        emitModelChange = false
      ) => {
        this.clearModelSubscriptions();
        this.formlyBuilder.buildForm(
          this.form,
          this.fields,
          this.model,
          this.options
        );
        this.trackModelChanges(this.fields);

        if (emitModelChange) {
          this.modelChange.emit((this._modelChangeValue = clone(this.model)));
        }
      };
    }
  }

  private checkExpressionChange() {
    if (this.options && (<KreFormOptionsCache>this.options)._checkField) {
      (<KreFormOptionsCache>this.options)._checkField({
        fieldGroup: this.fields,
        model: this.model,
        formControl: this.form,
        options: this.options,
      });
    }
  }

  private trackModelChanges(
    fields: KreFormFieldConfig[],
    rootKey: string[] = []
  ) {
    fields.forEach((field) => {
      field.status = this.status;
      if (field.key && !field.fieldGroup) {
        const control = field.formControl;
        let valueChanges = control.valueChanges.pipe(
          distinctUntilChanged((x, y) => {
            if (x !== y || Array.isArray(x) || isObject(x)) {
              return false;
            }

            return true;
          })
        );

        const { updateOn, debounce } = field.modelOptions;
        if (
          (!updateOn || updateOn === "change") &&
          debounce &&
          debounce.default > 0
        ) {
          valueChanges = control.valueChanges.pipe(
            debounceTime(debounce.default)
          );
        }

        this.modelChangeSubs.push(
          valueChanges.subscribe((value) => {
            if (
              control instanceof FormControl &&
              control["_fields"] &&
              control["_fields"].length > 1
            ) {
              control.patchValue(value, { emitEvent: false, onlySelf: true });
            }

            if (field.parsers && field.parsers.length > 0) {
              field.parsers.forEach((parserFn) => (value = parserFn(value)));
            }

            this.changeModel({
              key: [...rootKey, ...getKeyPath(field)].join("."),
              value,
              field,
            });
          })
        );
        const observers = control.valueChanges["observers"];
        if (observers && observers.length > 1) {
          observers.unshift(observers.pop());
        }
      }

      if (field.fieldGroup && field.fieldGroup.length > 0) {
        this.trackModelChanges(
          field.fieldGroup,
          field.key ? [...rootKey, ...getKeyPath(field)] : rootKey
        );
      }
    });
  }

  private clearModelSubscriptions() {
    this.modelChangeSubs.forEach((sub) => sub.unsubscribe());
    this.modelChangeSubs = [];
  }

  private get field(): any {
    return this.fields && this.fields[0] && this.fields[0].parent;
  }

  checkFieldShow(field: KreFormFieldConfig) {
    let result = true;
    if (this.status == 0) {
      if (field && field.hasOwnProperty("show")) {
        result = field.show;
      }
    }
    if (field.show == false) {
      field.hide = true;
    }
    return result;
  }
}
